/**
 * تقسيمُ مستندٍ على صفحات A4 فوق ورقة الشركة — بالقياس لا بالمتصفّح.
 *
 * طباعةُ المتصفّح لا تعرف «الورقة»: خلفيّةٌ ثابتةُ الموضع وحشوةٌ مكرّرة
 * (`box-decoration-break`) تعمل في Chrome الحديث وحده، وفي الإطار الداخليّ
 * (DocPreview) ونسخِ سطح المكتب كان الجدولُ يسيل فوق تذييل الورقة. فهنا
 * يُرسم المستند مرّةً في حاويةِ قياسٍ بعرض مساحة الكتابة، ثمّ يُوزَّع كتلةً
 * كتلةً على صفحاتٍ صريحةٍ بمقاس A4 — كلُّ صفحةٍ بورقتها وحدودِ كتابتها:
 *
 * - الكتلةُ التي تسعها الصفحة تُنسخ كما هي؛ والتي لا تسعها إن كانت حاويةً
 *   نزلنا إلى أبنائها (ونُسخ غلافُها في كلّ صفحة)، وإلّا انتقلت كلّها إلى
 *   صفحةٍ جديدة — فلا يُقسم سطرٌ ولا فقرة.
 * - الجدولُ يُقسم صفًّا صفًّا: رأسُه يتكرّر في كلّ صفحة، وعروضُ أعمدته
 *   مثبّتةٌ بما قيست فلا تقفز بين الصفحات، وآخرُ صفٍّ لا يفارق الإجمالي.
 * - `data-keep-with-prev` على كتلةٍ يُبقيها مع ما قبلها: ملاحظاتُ الجدول تتبع
 *   إجماليَّه إلى الصفحة التالية إن لم يتّسع لهما معًا.
 */

/** مليمترٌ بوحدات CSS — 96 نقطةً للبوصة. */
export const MM = 96 / 25.4;

const ATOMIC = new Set(['TR', 'THEAD', 'TFOOT', 'IMG', 'SVG', 'P', 'LI', 'H1', 'H2', 'H3', 'H4', 'BUTTON']);

function outer(el: Element): number {
  const cs = getComputedStyle(el);
  return (el as HTMLElement).offsetHeight + (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
}

/** حاويةٌ تُقسم؟ لها أكثرُ من ابنٍ عنصر، ولا نصَّ مباشرًا فيها يُفصل عن سياقه. */
function splittable(el: Element): boolean {
  if (ATOMIC.has(el.tagName) || el.hasAttribute('data-paper-keep')) return false;
  if (el.children.length === 0) return false;
  /* غلافٌ حول جدولٍ أو حاويةٍ واحدة يُفتح إليها. */
  if (el.children.length === 1) {
    const only = el.children[0]!;
    if (!(only instanceof HTMLTableElement) && !splittable(only)) return false;
  }
  for (const n of Array.from(el.childNodes)) if (n.nodeType === Node.TEXT_NODE && n.textContent?.trim()) return false;
  const display = getComputedStyle(el).display;
  /* الصفُّ الأفقيّ (flex/grid في سطر) يُقسم أعمدةً لا أسطرًا — يبقى كتلةً واحدة. */
  if (display.includes('grid')) return false;
  if (display.includes('flex') && !getComputedStyle(el).flexDirection.startsWith('column')) return false;
  return true;
}

/** ما بعد الكتلة من كتلٍ ملتصقةٍ بها (`data-keep-with-prev`) — يُحجز لها مكانٌ معها. */
function keepAfter(kids: Element[], i: number): number {
  let h = 0;
  for (let j = i + 1; j < kids.length && kids[j]!.hasAttribute('data-keep-with-prev'); j++) h += outer(kids[j]!);
  return h;
}

/**
 * يوزّع أبناءَ `src` (حاويةُ القياس بعرض مساحة الكتابة) على صفحاتٍ ارتفاعُ
 * كتابتها `avail` بكسل. يعيد لكلّ صفحةٍ حاويةً تحمل نُسخَ ما يُكتب فيها.
 */
export function paginate(src: HTMLElement, avail: number): HTMLElement[] {
  const pages: HTMLElement[] = [];
  /* أغلفةُ الحاويات المفتوحة الآن — تُنسخ في رأس كلّ صفحةٍ جديدة. */
  const stack: Array<{ src: Element; out: Element; chrome: number }> = [];
  let root!: HTMLElement;
  let used = 0;
  let placed = 0;

  const newPage = () => {
    root = document.createElement('div');
    pages.push(root);
    used = 0;
    placed = 0;
    let parent: Element = root;
    for (const s of stack) {
      const shell = s.src.cloneNode(false) as Element;
      parent.appendChild(shell);
      s.out = shell;
      parent = shell;
      used += s.chrome;
    }
  };
  const target = (): Element => (stack.length ? stack[stack.length - 1]!.out : root);
  const put = (node: Element, h: number) => {
    target().appendChild(node.cloneNode(true));
    used += h;
    placed++;
  };

  const splitTable = (table: HTMLTableElement, after: number) => {
    const cs = getComputedStyle(table);
    const margins = (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
    const head = table.tHead;
    const headH = head?.offsetHeight ?? 0;
    const foot = table.tFoot;
    const footH = foot?.offsetHeight ?? 0;
    const rows = Array.from(table.tBodies).flatMap((b) => Array.from(b.rows));

    /* عروضُ الأعمدة كما قيست: من آخر صفٍّ في الرأس، وإلّا من أوّل صفٍّ في الجسم. */
    const ref = head?.rows[head.rows.length - 1] ?? rows[0];
    const widths = ref ? Array.from(ref.cells).map((c) => ({ span: c.colSpan, w: c.getBoundingClientRect().width })) : [];
    const tableW = table.getBoundingClientRect().width;

    let body: HTMLTableSectionElement | null = null;
    const start = () => {
      const frag = table.cloneNode(false) as HTMLTableElement;
      frag.style.tableLayout = 'fixed';
      frag.style.width = `${tableW}px`;
      if (widths.length) {
        const cols = document.createElement('colgroup');
        for (const { span, w } of widths) {
          for (let k = 0; k < span; k++) {
            const col = document.createElement('col');
            col.style.width = `${w / span}px`;
            cols.appendChild(col);
          }
        }
        frag.appendChild(cols);
      }
      if (head) frag.appendChild(head.cloneNode(true));
      body = (table.tBodies[0]?.cloneNode(false) as HTMLTableSectionElement) ?? document.createElement('tbody');
      frag.appendChild(body);
      target().appendChild(frag);
      used += margins + headH;
      placed++;
      return frag;
    };

    let frag: HTMLTableElement | null = null;
    rows.forEach((row, i) => {
      const rh = row.offsetHeight;
      /* آخرُ صفٍّ يصحب الإجماليَّ وما التصق به: لا يُترك إجماليٌّ وحده في صفحة. */
      const need = rh + (i === rows.length - 1 ? footH + after : 0);
      if (!frag) {
        if (placed > 0 && used + margins + headH + need > avail) newPage();
        frag = start();
      } else if (used + need > avail) {
        newPage();
        frag = start();
      }
      body!.appendChild(row.cloneNode(true));
      used += rh;
    });
    if (!frag) frag = start();
    if (foot) {
      frag.appendChild(foot.cloneNode(true));
      used += footH;
    }
  };

  const layout = (el: Element, after: number) => {
    const h = outer(el);
    if (used + h + after <= avail || (placed === 0 && h + after > avail && !splittable(el) && el.tagName !== 'TABLE')) {
      put(el, h);
      return;
    }
    if (el instanceof HTMLTableElement && el.tBodies.length && el.tBodies[0]!.rows.length) {
      splitTable(el, after);
      return;
    }
    if (splittable(el)) {
      const kids = Array.from(el.children);
      const chrome = Math.max(0, h - kids.reduce((s, k) => s + outer(k), 0));
      const shell = el.cloneNode(false) as Element;
      target().appendChild(shell);
      used += chrome;
      stack.push({ src: el, out: shell, chrome });
      kids.forEach((k, i) => layout(k, i === kids.length - 1 ? after : keepAfter(kids, i)));
      stack.pop();
      return;
    }
    /* كتلةٌ لا تُقسم ولا تسعها البقيّة: إلى صفحةٍ جديدة كاملةً. */
    if (placed > 0) newPage();
    put(el, h);
  };

  newPage();
  const kids = Array.from(src.children);
  kids.forEach((k, i) => layout(k, keepAfter(kids, i)));
  return pages;
}
